/**
 * Image blob cache in IndexedDB. Once an image is downloaded, it's saved
 * as Blob in IndexedDB. Next time the same URL is requested, it loads
 * INSTANTLY from local storage (WhatsApp-style).
 *
 * Benefits:
 *  - Zero loading time for previously seen images
 *  - Works offline
 *  - Survives app restarts
 */

const DB_NAME = 'RVINCENTE_IMAGES_V1';
const DB_VERSION = 1;
const STORE = 'images';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

let _db = null;
let _dbPromise = null;

function openDB() {
  if (_db) return Promise.resolve(_db);
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'url' });
      }
    };
    req.onsuccess = (e) => { _db = e.target.result; _dbPromise = null; resolve(_db); };
    req.onerror = () => { _dbPromise = null; reject(req.error); };
  });
  return _dbPromise;
}

export async function getCachedImage(url) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(url);
      req.onsuccess = () => {
        const row = req.result;
        if (!row) return resolve(null);
        if (Date.now() - row.ts > MAX_AGE_MS) return resolve(null);
        resolve(row.blob);
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

export async function setCachedImage(url, blob) {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put({ url, blob, ts: Date.now() });
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch {
    return false;
  }
}
